import { useSignIn, useSignUp } from '@clerk/react';
import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { finalizeClerkSignIn } from '@/lib/auth/finalize-clerk-sign-in';
import { finalizeClerkSignUp } from '@/lib/auth/finalize-clerk-sign-up';

type AuthClerkTicketEffectProps = {
  signInRedirectUrl: string;
  signUpRedirectUrl: string;
};

/**
 * Consumes Clerk invitation / magic-link `?ticket=` params on the combined
 * `/login` screen (including redirects from legacy `/signup` and `/sign-up`).
 */
export function AuthClerkTicketEffect({
  signInRedirectUrl,
  signUpRedirectUrl,
}: AuthClerkTicketEffectProps) {
  const [searchParams] = useSearchParams();
  const ticket = searchParams.get('ticket');
  const navigate = useNavigate();
  const { signIn } = useSignIn();
  const { signUp } = useSignUp();
  const consumedRef = useRef(false);

  useEffect(() => {
    if (!ticket || consumedRef.current || !signIn || !signUp) return;
    consumedRef.current = true;

    const navigateApp = (path: string) => {
      navigate(path, { replace: true });
    };

    void (async () => {
      const signInAttempt = await signIn.ticket({ ticket });
      if (!signInAttempt.error && signIn.status === 'complete') {
        await finalizeClerkSignIn(signIn, signInRedirectUrl, navigateApp);
        return;
      }

      const signUpAttempt = await signUp.ticket({ ticket });
      if (!signUpAttempt.error && signUp.status === 'complete') {
        await finalizeClerkSignUp(signUp, signUpRedirectUrl, navigateApp);
      }
    })();
  }, [ticket, signIn, signUp, signInRedirectUrl, signUpRedirectUrl, navigate]);

  return null;
}
